import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  BarChart3,
  TrendingUp,
  Eye,
  Download,
  Loader2,
} from "lucide-react";
import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import DashboardLayout from "@/components/dashboard/DashboardLayout";

const Analytics = () => {
  const { user, loading } = useAuth();
  const { subscribed } = useSubscription();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const stats = [
    { label: "Total Thumbnails", value: "24", icon: BarChart3, change: "+12%" },
    { label: "Total Views", value: "1,234", icon: Eye, change: "+8%" },
    { label: "Downloads", value: "89", icon: Download, change: "+24%" },
    { label: "Avg CTR", value: "4.2%", icon: TrendingUp, change: "+0.5%" },
  ];

  return (
    <DashboardLayout activeTab="analytics">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Analytics</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Track your thumbnail performance
              </p>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6">
          {subscribed ? (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="p-6 rounded-xl bg-card border border-border"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <stat.icon className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-sm text-green-500 font-medium">
                        {stat.change}
                      </span>
                    </div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Chart Placeholder */}
              <div className="p-6 rounded-xl bg-card border border-border">
                <h3 className="font-semibold mb-4">Performance Over Time</h3>
                <div className="h-64 flex items-center justify-center bg-secondary/50 rounded-lg">
                  <p className="text-muted-foreground">
                    Chart visualization coming soon
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-xl gradient-bg flex items-center justify-center mb-4">
                <BarChart3 className="w-8 h-8 text-primary-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Unlock Analytics</h3>
              <p className="text-muted-foreground mb-6 text-center max-w-md">
                Upgrade to Pro to access detailed analytics and track your thumbnail performance
              </p>
              <Link to="/pricing">
                <Button variant="gradient">Upgrade to Pro</Button>
              </Link>
            </div>
          )}
        </div>
    </DashboardLayout>
  );
};

export default Analytics;
